import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  useSessionStatus,
  useCashflow,
  useExpenses,
  useAddCashflow,
  useAddExpense,
} from '../../hooks/useSession';
import { useAuthStore } from '../../stores/auth.store';
import { formatCurrency } from '../../lib/currency';
import type { Cashflow } from '@motra/shared';

type Tab = 'cashflow' | 'expenses';

export default function CashflowPanel() {
  const { t } = useTranslation();
  const shop = useAuthStore((s) => s.shop);
  const currSym = shop?.curr_sym ?? '$';

  const { data: session } = useSessionStatus();
  const sessionId = session?.id;

  const { data: cashflows = [] } = useCashflow(sessionId);
  const { data: expenses = [] } = useExpenses(sessionId);
  const addCashflow = useAddCashflow();
  const addExpense = useAddExpense();

  const [activeTab, setActiveTab] = useState<Tab>('cashflow');

  // Cash in/out form
  const [cfTitle, setCfTitle] = useState('');
  const [cfValue, setCfValue] = useState('');
  const [cfFlow, setCfFlow] = useState<'in' | 'out'>('in');
  const [cfType, setCfType] = useState('cash');

  // Expense form
  const [expTitle, setExpTitle] = useState('');
  const [expAmount, setExpAmount] = useState('');
  const [expCategory, setExpCategory] = useState('');

  const [error, setError] = useState('');

  async function handleAddCashflow(e: React.FormEvent) {
    e.preventDefault();
    if (!shop?.id) return;
    setError('');

    try {
      await addCashflow.mutateAsync({
        shopid: shop.id,
        session_id: sessionId,
        title: cfTitle,
        value: parseFloat(cfValue) || 0,
        flow: cfFlow,
        type: cfType,
      });
      setCfTitle('');
      setCfValue('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add cashflow entry');
    }
  }

  async function handleAddExpense(e: React.FormEvent) {
    e.preventDefault();
    if (!shop?.id) return;
    setError('');

    try {
      await addExpense.mutateAsync({
        shopid: shop.id,
        session_id: sessionId,
        title: expTitle,
        amount: parseFloat(expAmount) || 0,
        category: expCategory || undefined,
      });
      setExpTitle('');
      setExpAmount('');
      setExpCategory('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add expense');
    }
  }

  // Totals
  const totalIn = cashflows
    .filter((c: Cashflow) => c.flow === 'in')
    .reduce((sum: number, c: Cashflow) => sum + c.value, 0);
  const totalOut = cashflows
    .filter((c: Cashflow) => c.flow === 'out')
    .reduce((sum: number, c: Cashflow) => sum + c.value, 0);

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">{t('cashflow', 'Cash Flow')}</h1>
        <Link to="/session" className="btn btn--outline">
          {t('back', 'Back to Session')}
        </Link>
      </div>

      {/* Tab buttons */}
      <div className="tabs">
        <button
          className={`tab-btn${activeTab === 'cashflow' ? ' tab-btn--active' : ''}`}
          onClick={() => setActiveTab('cashflow')}
        >
          {t('cashflow', 'Cash Flow')}
        </button>
        <button
          className={`tab-btn${activeTab === 'expenses' ? ' tab-btn--active' : ''}`}
          onClick={() => setActiveTab('expenses')}
        >
          {t('expenses', 'Expenses')}
        </button>
      </div>

      {activeTab === 'cashflow' && (
        <div className="cashflow-panel">
          {/* Summary */}
          <div className="cashflow-summary">
            <div className="cashflow-summary__item cashflow-summary__item--in">
              <span>Total In</span>
              <span>{formatCurrency(totalIn, currSym)}</span>
            </div>
            <div className="cashflow-summary__item cashflow-summary__item--out">
              <span>Total Out</span>
              <span>{formatCurrency(totalOut, currSym)}</span>
            </div>
            <div className="cashflow-summary__item cashflow-summary__item--net">
              <span>Net</span>
              <span>{formatCurrency(totalIn - totalOut, currSym)}</span>
            </div>
          </div>

          {/* Add form */}
          <form onSubmit={handleAddCashflow} className="cashflow-form">
            <h3>Add Entry</h3>
            <div className="form-grid form-grid--3col">
              <div className="form-group">
                <label className="form-label">Title</label>
                <input
                  type="text"
                  className="form-input"
                  value={cfTitle}
                  onChange={(e) => setCfTitle(e.target.value)}
                  required
                  placeholder="Description"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Amount</label>
                <input
                  type="number"
                  className="form-input"
                  value={cfValue}
                  onChange={(e) => setCfValue(e.target.value)}
                  required
                  min={0}
                  step="0.01"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Flow</label>
                <select className="form-select" value={cfFlow} onChange={(e) => setCfFlow(e.target.value as 'in' | 'out')}>
                  <option value="in">Cash In</option>
                  <option value="out">Cash Out</option>
                </select>
              </div>
            </div>
            {error && <div className="alert alert--error">{error}</div>}
            <button type="submit" className="btn btn--primary" disabled={addCashflow.isPending}>
              Add
            </button>
          </form>

          {/* List */}
          <div className="cashflow-list">
            {cashflows.map((c: Cashflow) => (
              <div key={c.id} className={`cashflow-entry cashflow-entry--${c.flow}`}>
                <span className="cashflow-entry__title">{c.title}</span>
                <span className="cashflow-entry__time">
                  {c.created_at ? new Date(c.created_at).toLocaleTimeString() : ''}
                </span>
                <span className="cashflow-entry__amount">
                  {c.flow === 'in' ? '+' : '−'}{formatCurrency(c.value, currSym)}
                </span>
              </div>
            ))}
            {cashflows.length === 0 && <p className="text-muted">No cash flow entries yet.</p>}
          </div>
        </div>
      )}

      {activeTab === 'expenses' && (
        <div className="expenses-panel">
          {/* Add form */}
          <form onSubmit={handleAddExpense} className="cashflow-form">
            <h3>{t('add', 'Add')} {t('expenses', 'Expense')}</h3>
            <div className="form-grid form-grid--3col">
              <div className="form-group">
                <label className="form-label">Title</label>
                <input
                  type="text"
                  className="form-input"
                  value={expTitle}
                  onChange={(e) => setExpTitle(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Amount</label>
                <input
                  type="number"
                  className="form-input"
                  value={expAmount}
                  onChange={(e) => setExpAmount(e.target.value)}
                  required
                  min={0}
                  step="0.01"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Category</label>
                <input
                  type="text"
                  className="form-input"
                  value={expCategory}
                  onChange={(e) => setExpCategory(e.target.value)}
                  placeholder="e.g. utilities"
                />
              </div>
            </div>
            {error && <div className="alert alert--error">{error}</div>}
            <button type="submit" className="btn btn--primary" disabled={addExpense.isPending}>
              Add Expense
            </button>
          </form>

          {/* List */}
          <div className="cashflow-list">
            {expenses.map((exp) => (
              <div key={exp.id} className="cashflow-entry cashflow-entry--out">
                <span className="cashflow-entry__title">{exp.title}</span>
                <span className="cashflow-entry__category">{exp.category ?? ''}</span>
                <span className="cashflow-entry__amount">
                  −{formatCurrency(exp.amount, currSym)}
                </span>
              </div>
            ))}
            {expenses.length === 0 && <p className="text-muted">No expenses recorded yet.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
